import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, setAdminToken, getAdminToken } from "@/lib/api";
import { COMPANY } from "@/data/products";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (getAdminToken()) {
      // Try verify token by calling /me; if ok, skip login.
      api
        .get("/admin/me", {
          headers: { Authorization: `Bearer ${getAdminToken()}` },
        })
        .then(() => navigate("/admin/dashboard"))
        .catch(() => {});
    }
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      setAdminToken(data.token);
      toast.success("Authenticated");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-admin-login" className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="w-full max-w-md border border-zinc-800 p-10 bg-[var(--eel-surface)]">
        <div className="flex items-center gap-4 mb-6">
          <img
            src={COMPANY.logoUrl}
            alt="EELLEVEN MINING"
            className="w-14 h-14 rounded-full ring-1 ring-zinc-700"
          />
          <div className="w-12 h-12 border border-zinc-700 grid place-items-center">
            <Lock size={18} className="copper-text" />
          </div>
        </div>
        <p className="label-eyebrow">/ Admin</p>
        <h1 className="font-heading mt-2 text-3xl font-black uppercase tracking-tighter">
          Secure Access
        </h1>
        <p className="mt-3 text-sm text-zinc-400">
          Restricted area. Authorised personnel only.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label
              className="label-eyebrow text-zinc-400 block mb-2"
              htmlFor="admin-password"
            >
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-sm text-zinc-100 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full px-6 py-3 copper-bg text-black font-semibold uppercase tracking-widest text-sm hover:bg-[var(--eel-copper-hover)] transition-colors disabled:opacity-60"
          >
            {loading ? "Verifying..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
